import { useCallback, useEffect, useRef } from "react";
import { getImageScale } from "tools/richText/getImageScale";
import { getImageExtension } from "tools/richText/getImageExtension";
import { Image } from "../types";

export const imageClass = "rich-text-image";

type Params = {
  imagesUpdateCounter: number;
  setImage: (image: Image) => void;
  editTooltipElement: HTMLDivElement | null;
  rootEditorElement: HTMLDivElement | null;
};

function useImagesEdit({
  imagesUpdateCounter,
  setImage,
  editTooltipElement,
  rootEditorElement,
}: Params) {
  // This hook is called unconditionally in RichTextEditor's render body, so
  // a raw `document.addEventListener` here (not inside an effect, no
  // cleanup) registered a brand-new "mousemove" listener on EVERY render of
  // the editor — never removed. With a form that re-renders often (any
  // field change, any watch() firing elsewhere), this was the dominant
  // source of an unbounded, ever-growing document listener count over a
  // session (confirmed via Chrome's Performance Monitor: listeners went
  // from ~2k to 160k+).
  useEffect(() => {
    const handleMoveTooltip = (event: MouseEvent) => {
      if (!rootEditorElement || !editTooltipElement) {
        return;
      }

      const rootElementRect = rootEditorElement.getBoundingClientRect();
      const offsetTop = 28;
      const offsetLeft = 10;
      const mouseY = event.clientY - rootElementRect.top - offsetTop;
      const mouseX = event.clientX - rootElementRect.left + offsetLeft;

      editTooltipElement.style.top = `${mouseY}px`;
      editTooltipElement.style.left = `${mouseX}px`;
    };
    const onMouseMove = (e: MouseEvent) => {
      e.stopPropagation();
      handleMoveTooltip(e);
    };

    document.addEventListener("mousemove", onMouseMove, false);
    return () => {
      document.removeEventListener("mousemove", onMouseMove, false);
    };
  }, [rootEditorElement, editTooltipElement]);

  // Tracks which <img> nodes already have their handlers, so repeated passes
  // wire each one only once. Deliberately NOT a data-attribute: `handleChange`
  // persists `root.innerHTML` verbatim, so any marker written into the DOM
  // would end up in the saved HTML and, on the next load, make the image look
  // already-wired — reintroducing the very "no edit option" bug this covers.
  const wiredImages = useRef(new WeakSet<HTMLImageElement>());

  const prepareImages = useCallback(() => {
    const allImages = document.querySelectorAll<HTMLImageElement>(
      `img.${imageClass}`
    );

    allImages.forEach((image) => {
      // Re-applied on every pass, because it has to follow a swapped src (e.g.
      // a pending blob replaced by its storage URL).
      //
      // Written ONLY when it actually changes, and never at all for the default
      // scale: every style write on an <img> is a mutation Quill's blot can't
      // account for (`formats/image` knows `alt` / `height` / `width` only), so
      // its observer rebuilds the blot and the caret jumps to the image. An
      // unscaled image now gets no inline style at all, which is the common case.
      const initialScale = getImageScale(image.src);
      const nextWidth =
        initialScale && initialScale !== 100 ? `${initialScale}%` : "";
      if (image.style.width !== nextWidth) {
        image.style.width = nextWidth;
      }

      // Wiring happens once per node. Re-attaching on every pass used to pile
      // up duplicate listeners on the same image.
      if (wiredImages.current.has(image)) {
        return;
      }
      wiredImages.current.add(image);

      image.addEventListener("mouseenter", () => {
        editTooltipElement?.classList.add("show");
      });

      image.addEventListener("mouseleave", () => {
        editTooltipElement?.classList.remove("show");
      });

      image.addEventListener("click", () => {
        // Hand the modal the node's own src instead of re-downloading the
        // file into a data URI first.
        //
        // The old `fetch(image.src)` + FileReader ran BEFORE `setImage`, so
        // any failure meant the modal simply never opened — silently, since
        // that listener was async and nothing caught its rejection.
        //
        // Nothing on the resize path needs the bytes anyway — `ResizeImage`
        // is a pure CSS `transform: scale()` preview. Only `CropImage`
        // touches pixels, and `EditImageModal` inlines them on demand.
        setImage({
          dataString: image.src,
          extension: getImageExtension(image.src),
          scale: getImageScale(image.src) ?? 100,
          element: image,
        });
      });
    });
  }, [editTooltipElement, setImage]);

  useEffect(() => {
    prepareImages();
  }, [imagesUpdateCounter, prepareImages]);

  // `imagesUpdateCounter` alone is not enough: it is bumped only by the
  // toolbar's insert flow (`handleAddImage`) and by the immediate/Firebase
  // upload scan. An image arriving through paste or drop with a deferred
  // adapter bumped nothing, so it never got an id or a click handler and
  // simply had no edit option — until an unrelated toolbar insert happened to
  // re-run this pass and wire it retroactively. Observing the DOM covers every
  // way an <img> can appear.
  useEffect(() => {
    const target = rootEditorElement ?? document.body;
    const containsImage = (node: Node) =>
      node instanceof HTMLElement &&
      (node.matches(`img.${imageClass}`) ||
        node.querySelector(`img.${imageClass}`) !== null);
    const observer = new MutationObserver((records) => {
      // Only re-scan when an <img> actually joined the document. Typing emits
      // plenty of childList records, and none of them need a pass.
      const hasNewImage = records.some((record) =>
        Array.from(record.addedNodes).some(containsImage)
      );
      if (hasNewImage) {
        prepareImages();
      }
    });
    observer.observe(target, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [rootEditorElement, prepareImages]);
}

export default useImagesEdit;
